'use client';

import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Sparkles, FormInput, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface CreateProjectOptionDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (option: 'wizard' | 'standard') => void;
}

export const CreateProjectOptionDialog: React.FC<CreateProjectOptionDialogProps> = ({
    open,
    onOpenChange,
    onSelect,
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px] w-[95vw] md:w-full border-none shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] rounded-[24px] md:rounded-[32px] p-0 overflow-hidden bg-background/80 backdrop-blur-2xl ring-1 ring-white/20 flex flex-col max-h-[85vh]">
                <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-5 md:p-8 border-b relative shrink-0">
                    <div className="absolute top-0 right-0 p-5 md:p-6 opacity-10 pointer-events-none">
                        <Sparkles className="w-14 h-14 md:w-20 md:h-20 text-primary" />
                    </div>
                    <DialogHeader className="relative z-10 text-left">
                        <DialogTitle className="text-xl md:text-2xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
                            How would you like to build?
                        </DialogTitle>
                        <DialogDescription className="text-xs md:text-base font-medium text-muted-foreground/90 mt-1">
                            Choose the workflow that fits your needs today.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="p-5 md:p-9 space-y-3.5 md:space-y-4 flex-1 overflow-y-auto custom-scrollbar">
                    <button
                        onClick={() => onSelect('wizard')}
                        className="w-full flex items-center gap-4 md:gap-5 p-4 md:p-6 rounded-[18px] md:rounded-[24px] border-2 border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-all duration-300 group text-left relative overflow-hidden active:scale-[0.98] shadow-sm hover:shadow-xl hover:shadow-primary/10"
                    >
                        <div className="w-11 h-11 md:w-15 md:h-15 rounded-[14px] md:rounded-[18px] bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30 group-hover:scale-110 transition-all duration-500 group-hover:rotate-3">
                            <Sparkles className="w-5 h-5 md:w-7 md:h-7" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                                <h3 className="font-bold text-base md:text-xl text-primary tracking-tight">Quick Wizard</h3>
                                <Badge className="bg-primary/20 text-primary hover:bg-primary/20 text-[8px] md:text-[9px] font-bold tracking-widest px-2 py-0 border-none">POPULAR</Badge>
                            </div>
                            <p className="text-xs md:text-sm text-foreground/70 font-medium leading-relaxed">Step-by-step guided experience. Perfect for new projects.</p>
                        </div>
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-primary opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-[-10px] group-hover:translate-x-0" />

                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                    </button>

                    <button
                        onClick={() => onSelect('standard')}
                        className="w-full flex items-center gap-4 md:gap-5 p-4 md:p-6 rounded-[18px] md:rounded-[24px] border-2 border-muted bg-background/50 hover:bg-muted/30 hover:border-muted-foreground/30 transition-all duration-300 group text-left active:scale-[0.98] shadow-sm hover:shadow-md"
                    >
                        <div className="w-11 h-11 md:w-15 md:h-15 rounded-[14px] md:rounded-[18px] bg-muted text-muted-foreground flex items-center justify-center group-hover:bg-muted-foreground group-hover:text-background transition-all duration-500">
                            <FormInput className="w-5 h-5 md:w-7 md:h-7" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-base md:text-xl tracking-tight mb-0.5 text-foreground">Standard Mode</h3>
                            <p className="text-xs md:text-sm text-muted-foreground font-medium leading-relaxed">Full control with one-by-one manual entry.</p>
                        </div>
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-[-10px] group-hover:translate-x-0" />
                    </button>
                </div>

                <div className="p-3 md:p-5 bg-muted/40 text-center border-t border-white/10 shrink-0">
                    <p className="text-[9px] md:text-[10px] font-bold text-muted-foreground/60 tracking-[0.2em] uppercase">Built for efficiency & speed</p>
                </div>
            </DialogContent>
        </Dialog>
    );
};
